import { useDispatch, useSelector } from "react-redux";

import { Box, Flex, Separator, Text, VStack } from "@chakra-ui/react";

import OAuthConnector from "@components/oauth-connector";

import { fetchInit, selectInit } from "@state/mondaySlice";
import type { AppDispatch } from "@state/store";

type Props = {
  isMondayConnected: boolean;
  isGoogleConnected: boolean;
};

const ReconnectPage = ({ isMondayConnected, isGoogleConnected }: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const { isNewUser } = useSelector(selectInit);

  const onOauthConnect = async () => {
    await dispatch(fetchInit({ forceRefetch: true }));
  };

  const onOauthDisconnect = async () => await dispatch(fetchInit({ forceRefetch: true }));

  const shouldShowMonday = !isMondayConnected;
  const shouldShowGoogle = !isGoogleConnected;

  const description = isNewUser
    ? "Let's get started by connecting your monday.com account."
    : !isMondayConnected && !isGoogleConnected
      ? "Both your monday.com and Google connections have expired. Please reconnect to continue."
      : !isMondayConnected
        ? "Your monday.com connection has expired. Please reconnect to continue."
        : "Your Google connection has expired. Please reconnect to continue.";

  return (
    <Flex
      flexDir="column"
      alignItems="center"
      justifyContent="center"
      p="15px"
      bg="gray.100"
      height="100vh"
    >
      <Flex
        direction="column"
        flex="1"
        maxW="5xl"
      >
        <Box
          textAlign="center"
          w="full"
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
          >
            {isNewUser ? "Connect Your Account" : "Reconnection Required"}
          </Text>
          <Text
            fontSize="md"
            color="gray.600"
          >
            {description}
          </Text>
          <Separator my="24px" />
        </Box>

        <VStack gap="10px">
          {shouldShowMonday && (
            <VStack
              align="flex-start"
              alignSelf="center"
              gapY="24px"
              bg="white"
              borderRadius="lg"
              shadow="xs"
              p={6}
              w={{ base: "full", lg: "752px" }}
              maxW="752px"
            >
              <OAuthConnector
                type="monday"
                data={{ isMondayConnected, isGoogleConnected }}
                isLoading={false}
                oAuthCallback={onOauthConnect}
                oAuthDisconnectCallback={onOauthDisconnect}
              />
            </VStack>
          )}

          {shouldShowGoogle && (
            <VStack
              align="flex-start"
              alignSelf="center"
              gapY="24px"
              bg="white"
              borderRadius="lg"
              shadow="xs"
              p={6}
              w={{ base: "full", lg: "752px" }}
              maxW="752px"
            >
              <OAuthConnector
                type="google"
                data={{ isMondayConnected, isGoogleConnected }}
                isLoading={false}
                oAuthCallback={onOauthConnect}
                oAuthDisconnectCallback={onOauthDisconnect}
              />
            </VStack>
          )}
        </VStack>
      </Flex>
    </Flex>
  );
};

export default ReconnectPage;
